import React from 'react';
import { type LayerConfig, CentersViewConfig, DatapointConfig, DrivetimeAggregationConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { ViewSettings } from '../../../types/metadata';
import { metadataService } from '../../../services/metadataService';

interface ViewLayerConfigProps {
  layer: LayerConfig;
  settings: ViewSettings | undefined;
}

type ViewType = 'none' | 'centers' | 'pcp' | 'ml';

export const ViewLayerConfig: React.FC<ViewLayerConfigProps> = ({ layer, settings }) => {
  const updateLayerConfig = useMapStore((state) => state.updateLayerConfig);
  const layersConfig = useMapStore((state) => state.layersConfig);

  const handleViewTypeChange = (type: ViewType) => {
    if (type === 'none') {
      updateLayerConfig(layer.id, {
        viewSettings: undefined
      });
    } else if (type === 'centers') {
      updateLayerConfig(layer.id, {
        viewSettings: { 
          type,
          centersConfig: {
            datapoints: [],
            aggregationMode: 'drivetime',
            drivetimePolygons: {
              minRange: 5,
              maxRange: 25,
              step: 1
            },
            radiusConfig: {
              radius: 10,
              steps: 5
            },
            drivetimeAggregations: []
          }
        }
      });
    } else {
      updateLayerConfig(layer.id, {
        viewSettings: { type }
      });
    }
  };

  const currentViewType = settings?.type || 'none';
  
  // Ensure centersConfig exists when view type is centers
  const centersConfig = React.useMemo(() => {
    if (currentViewType === 'centers') {
      return settings?.centersConfig || {
        datapoints: [],
        drivetimeAggregations: []
      };
    }
    return settings?.centersConfig;
  }, [currentViewType, settings?.centersConfig]);
  
  // Initialize centersConfig if view type is centers but config doesn't exist
  const hasInitialized = React.useRef(false);
  React.useEffect(() => {
    if (currentViewType === 'centers' && !settings?.centersConfig && !hasInitialized.current) {
      hasInitialized.current = true;
      updateLayerConfig(layer.id, {
        viewSettings: {
          type: 'centers',
          centersConfig: {
            datapoints: [],
            aggregationMode: 'drivetime',
            drivetimePolygons: {
              minRange: 5,
              maxRange: 25,
              step: 1
            },
            radiusConfig: {
              radius: 10,
              steps: 5
            },
            drivetimeAggregations: []
          }
        }
      });
    }
    
    // Reset the flag when view type changes away from centers
    if (currentViewType !== 'centers') {
      hasInitialized.current = false;
    }
  }, [currentViewType, settings?.centersConfig, layer.id, updateLayerConfig]);

  // Get field names from schema
  const fieldNames = Object.keys(layer.schema || {});

  // Get all available layers from metadata (not just loaded ones)
  const [allAvailableLayers, setAllAvailableLayers] = React.useState<LayerConfig[]>([]);
  
  React.useEffect(() => {
    const fetchAllLayers = async () => {
      try {
        const metadata = await metadataService.listMetadata();
        setAllAvailableLayers(metadata.files);
      } catch (error) {
        console.error('[ViewLayerConfig] Failed to fetch all layers:', error);
      }
    };
    
    fetchAllLayers();
  }, []);

  const updateCentersConfig = (updates: Partial<CentersViewConfig>) => {
    updateLayerConfig(layer.id, {
      viewSettings: {
        ...settings!,
        centersConfig: {
          ...centersConfig!,
          ...updates
        }
      }
    });
  };

  const addDatapoint = () => {
    const newDatapoint: DatapointConfig = { label: '', field: fieldNames[0] || '' };
    updateCentersConfig({
      datapoints: [...(centersConfig?.datapoints || []), newDatapoint]
    });
  };

  const updateDatapoint = (index: number, updates: Partial<DatapointConfig>) => {
    const datapoints = [...(centersConfig?.datapoints || [])];
    datapoints[index] = { ...datapoints[index], ...updates };
    updateCentersConfig({ datapoints });
  };

  const removeDatapoint = (index: number) => {
    const datapoints = (centersConfig?.datapoints || []).filter((_, i) => i !== index);
    updateCentersConfig({ datapoints });
  };

  const addDrivetimeAggregation = () => {
    const firstLayer = allAvailableLayers[0];
    if (!firstLayer) return;
    
    const newAggregation: DrivetimeAggregationConfig = {
      label: firstLayer.name,
      layerId: firstLayer.id,
      version: firstLayer.version
    };
    updateCentersConfig({
      drivetimeAggregations: [...(centersConfig?.drivetimeAggregations || []), newAggregation]
    });
  };

  const updateDrivetimeAggregation = (index: number, updates: Partial<DrivetimeAggregationConfig>) => {
    const aggregations = [...(centersConfig?.drivetimeAggregations || [])];
    aggregations[index] = { ...aggregations[index], ...updates };
    updateCentersConfig({ drivetimeAggregations: aggregations });
  };

  const removeDrivetimeAggregation = (index: number) => {
    const aggregations = (centersConfig?.drivetimeAggregations || []).filter((_, i) => i !== index);
    updateCentersConfig({ drivetimeAggregations: aggregations });
  };

  return (
    <>
      <div className="marker-type-section">
        <h3>View Type</h3>
        <div className="view-type-dropdown">
          <select
            value={currentViewType}
            onChange={(e) => handleViewTypeChange(e.target.value as ViewType)}
            className="view-type-select"
          >
            <option value="none">None</option>
            <option value="centers">Centers</option>
            <option value="pcp">PCP</option>
            <option value="ml">ML</option>
          </select>
        </div>
      </div>

      {currentViewType === 'centers' && centersConfig && (
        <div className="centers-config-section">
          <h3>Centers Configuration</h3>
          
          {/* Title Field */}
          <div className="config-group">
            <label>Title Field</label>
            <select
              value={centersConfig.titleField || ''}
              onChange={(e) => updateCentersConfig({ titleField: e.target.value || undefined })}
              className="field-select"
            >
              <option value="">-- Select Field --</option>
              {fieldNames.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>

          {/* Address Field */}
          <div className="config-group">
            <label>Address Field</label>
            <select
              value={centersConfig.addressField || ''}
              onChange={(e) => updateCentersConfig({ addressField: e.target.value || undefined })}
              className="field-select"
            >
              <option value="">-- Select Field --</option>
              {fieldNames.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>

          {/* Datapoints */}
          <div className="config-group">
            <div className="config-group-header">
              <label>Datapoints</label>
              <button onClick={addDatapoint} className="add-btn">+ Add Datapoint</button>
            </div>
            {centersConfig.datapoints.map((datapoint, index) => (
              <div key={index} className="datapoint-item">
                <input
                  type="text"
                  placeholder="Label"
                  value={datapoint.label}
                  onChange={(e) => updateDatapoint(index, { label: e.target.value })}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="datapoint-label-input"
                />
                <select
                  value={datapoint.field}
                  onChange={(e) => updateDatapoint(index, { field: e.target.value })}
                  className="datapoint-field-select"
                >
                  {fieldNames.map(field => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
                <button onClick={() => removeDatapoint(index)} className="remove-btn">×</button>
              </div>
            ))}
          </div>

          {/* Aggregation Mode Toggle */}
          <div className="config-group">
            <label>Aggregation Mode</label>
            <div className="aggregation-mode-toggle">
              <button
                className={`toggle-btn ${(centersConfig.aggregationMode || 'drivetime') === 'drivetime' ? 'active' : ''}`}
                onClick={() => updateCentersConfig({ aggregationMode: 'drivetime' })}
              >
                Drivetime Polygons
              </button>
              <button
                className={`toggle-btn ${centersConfig.aggregationMode === 'radius' ? 'active' : ''}`}
                onClick={() => updateCentersConfig({ aggregationMode: 'radius' })}
              >
                Circle
              </button>
            </div>
          </div>

          {/* Drivetime Polygons Config */}
          {(centersConfig.aggregationMode || 'drivetime') === 'drivetime' && (
            <div className="config-group">
              <label>Drivetime Polygons</label>
              <div className="drivetime-polygon-config">
                <div className="range-inputs">
                  <div className="range-input-group">
                    <label>Min Range (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={centersConfig.drivetimePolygons?.minRange || 5}
                      onChange={(e) => updateCentersConfig({
                        drivetimePolygons: {
                          ...centersConfig.drivetimePolygons,
                          minRange: parseInt(e.target.value) || 5,
                          maxRange: centersConfig.drivetimePolygons?.maxRange || 25,
                          step: centersConfig.drivetimePolygons?.step || 1
                        }
                      })}
                      className="range-input"
                    />
                  </div>
                  <div className="range-input-group">
                    <label>Max Range (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={centersConfig.drivetimePolygons?.maxRange || 25}
                      onChange={(e) => updateCentersConfig({
                        drivetimePolygons: {
                          ...centersConfig.drivetimePolygons,
                          minRange: centersConfig.drivetimePolygons?.minRange || 5,
                          maxRange: parseInt(e.target.value) || 25,
                          step: centersConfig.drivetimePolygons?.step || 1
                        }
                      })}
                      className="range-input"
                    />
                  </div>
                  <div className="range-input-group">
                    <label>Step (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={centersConfig.drivetimePolygons?.step || 1}
                      onChange={(e) => updateCentersConfig({
                        drivetimePolygons: {
                          ...centersConfig.drivetimePolygons,
                          minRange: centersConfig.drivetimePolygons?.minRange || 5,
                          maxRange: centersConfig.drivetimePolygons?.maxRange || 25,
                          step: parseInt(e.target.value) || 1
                        }
                      })}
                      className="range-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Circle Radius Config */}
          {centersConfig.aggregationMode === 'radius' && (
            <div className="config-group">
              <label>Circle Radius</label>
              <div className="drivetime-polygon-config">
                <div className="range-inputs">
                  <div className="range-input-group">
                    <label>Radius (miles)</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={centersConfig.radiusConfig?.radius || 10}
                      onChange={(e) => updateCentersConfig({
                        radiusConfig: {
                          radius: parseFloat(e.target.value) || 10,
                          steps: centersConfig.radiusConfig?.steps || 5
                        }
                      })}
                      className="range-input"
                    />
                  </div>
                  <div className="range-input-group">
                    <label>Steps (buckets)</label>
                    <input
                      type="number"
                      min="2"
                      value={centersConfig.radiusConfig?.steps || 5}
                      onChange={(e) => updateCentersConfig({
                        radiusConfig: {
                          radius: centersConfig.radiusConfig?.radius || 10,
                          steps: parseInt(e.target.value) || 5
                        }
                      })}
                      className="range-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Drivetime Aggregations */}
          <div className="config-group">
            <div className="config-group-header">
              <label>Drivetime Aggregations</label>
              <button 
                onClick={addDrivetimeAggregation} 
                className="add-btn"
                disabled={allAvailableLayers.length === 0}
              >
                + Add Aggregation
              </button>
            </div>
            {centersConfig.drivetimeAggregations.map((aggregation, index) => {
              // Try to get layer from loaded layers first, then from all available layers
              const selectedLayer = layersConfig[aggregation.layerId] || 
                                   allAvailableLayers.find(l => l.id === aggregation.layerId);
              const aggregationFieldNames = selectedLayer ? Object.keys(selectedLayer.schema || {}) : [];
              
              return (
                <div key={index} className="aggregation-item">
                  <div className="aggregation-label-input">
                    <label>Label</label>
                    <input
                      type="text"
                      placeholder="Label"
                      value={aggregation.label}
                      onChange={(e) => updateDrivetimeAggregation(index, { label: e.target.value })}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="aggregation-input"
                    />
                  </div>
                  <div className="aggregation-layer-select">
                    <label>Layer</label>
                    <select
                      value={aggregation.layerId}
                      onChange={(e) => {
                        const newLayer = layersConfig[e.target.value] || 
                                        allAvailableLayers.find(l => l.id === e.target.value);
                        const oldLayer = layersConfig[aggregation.layerId] || 
                                        allAvailableLayers.find(l => l.id === aggregation.layerId);
                        // Update label if it was empty or matched the old layer name
                        const shouldUpdateLabel = !aggregation.label || aggregation.label === oldLayer?.name;
                        updateDrivetimeAggregation(index, {
                          layerId: e.target.value,
                          version: newLayer?.version || 1,
                          aggregationField: undefined,
                          ...(shouldUpdateLabel && newLayer ? { label: newLayer.name } : {})
                        });
                      }}
                      className="aggregation-select"
                    >
                      {allAvailableLayers.map(l => (
                        <option key={l.id} value={l.id}>{l.name} (v{l.version})</option>
                      ))}
                    </select>
                  </div>
                  <div className="aggregation-field-select">
                    <label>Aggregation Field (optional)</label>
                    <select
                      value={aggregation.aggregationField || ''}
                      onChange={(e) => updateDrivetimeAggregation(index, {
                        aggregationField: e.target.value || undefined
                      })}
                      className="aggregation-select"
                    >
                      <option value="">-- Count Only --</option>
                      {aggregationFieldNames.map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                  </div>
                  <button onClick={() => removeDrivetimeAggregation(index)} className="remove-btn">×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
